import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useApiKey } from '../context/ApiKeyContext';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
`;

const Notice = styled.p`
  width: 100%;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background-color: #fff5f8;
  border: 1px solid #f8bbd0;
  color: #ad1457;
  font-size: 0.9rem;
  text-align: center;
`;

const TryButton = styled.button`
  width: 100%;
  padding: 0.85rem;
  background-color: #e91e63;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #d81b60;
  }
`;

const TrySubtext = styled.p`
  margin-top: 0.5rem;
  font-size: 0.8rem;
  color: #999;
  text-align: center;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 1.5rem 0;
  color: #bbb;
  font-size: 0.85rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid #eee;
  }

  &::before { margin-right: 0.75rem; }
  &::after { margin-left: 0.75rem; }
`;

const Description = styled.p`
  margin-bottom: 1.5rem;
  text-align: center;
  color: #555;

  a {
    color: #e91e63;
    text-decoration: none;
  }
`;

const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Input = styled.input`
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem;
  background-color: white;
  color: #e91e63;
  border: 1px solid #e91e63;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #fff5f8;
  }
`;

const StorageNote = styled.p`
  margin-top: 0.75rem;
  font-size: 0.8rem;
  color: #999;
  text-align: center;

  a {
    color: #999;
    text-decoration: underline;
  }
`;

const ApiKeyInput = ({ notice }) => {
  const { saveApiKey, startTrial } = useApiKey();
  const [inputKey, setInputKey] = useState('');
  const [trialAvailable, setTrialAvailable] = useState(false);

  // The trial is only offered when the server has the owner's key configured.
  // A prior "trial exhausted" notice means we no longer offer it either.
  useEffect(() => {
    if (notice) return;
    let active = true;
    fetch('/api/trial/status', { credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (active && data) setTrialAvailable(!!data.available && data.remaining > 0);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [notice]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputKey.trim()) {
      saveApiKey(inputKey.trim());
    }
  };

  return (
    <Container>
      {notice && <Notice>{notice}</Notice>}

      {trialAvailable && (
        <>
          <TryButton onClick={startTrial}>Try it out — 2 free chats</TryButton>
          <TrySubtext>No API key needed. Uses the owner's key for a couple of practice chats.</TrySubtext>
          <Divider>or use your own key</Divider>
        </>
      )}

      <Description>
        You'll need an Anthropic API key.{' '}
        <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer">
          Get one here
        </a>.
      </Description>
      <Form onSubmit={handleSubmit}>
        <Input
          type="password"
          placeholder="Enter your Anthropic API key"
          value={inputKey}
          onChange={(e) => setInputKey(e.target.value)}
          required
        />
        <Button type="submit">Save API Key</Button>
      </Form>

      <StorageNote>
        Your key is stored only in your browser, never sent to our servers.{' '}
        <Link to="/privacy">Privacy Notice</Link>
      </StorageNote>
    </Container>
  );
};

export default ApiKeyInput;
